import { Link, useActionData, useRouteLoaderData } from "@remix-run/react";
import { useEffect, useMemo } from "react";
import { Modal } from "react-daisyui";
import { useTranslation } from "react-i18next";

import { IProjectDetailContactSaleModalProps } from "./types";

import Button from "~/components/atoms/Button";
import IconClose from "~/components/atoms/Icons/IconClose";
import { Input } from "~/components/atoms/Input";
import { SingleSelect } from "~/components/atoms/SelectBox";
import Typo from "~/components/atoms/Typo";
import CheckboxGroup from "~/components/molecules/CheckboxGroup/CheckboxGroup";
import { IOption } from "~/components/molecules/CheckboxGroup/type";
import { Form } from "~/components/templates/form";
import { action } from "~/routes/($lang).projects.$slug/route";
import useAppState from "~/stores";
import { IRouteLoaderData } from "~/types/routeLoaderData";
import { cn } from "~/utilities/cn";
import {
  COUNTRY_PHONE_NUMBER_DIALCODE,
  OPTIONS_CONTACT_SALE_INTERESTED,
} from "~/utilities/constants/common";
import { ETypoColor } from "~/utilities/enums/Colors";
import { Slug } from "~/utilities/enums/Slug";
import { ETypoTag, ETypoVariant } from "~/utilities/enums/Typo";
import {
  generateDateOptions,
  generateTimeOptions,
} from "~/utilities/helpers/dateTime";
import {
  contactSalesSchema,
  TContactSaleFormData,
} from "~/utilities/schema/contact-sale";

const ProjectDetailContactSale: React.FC<
  IProjectDetailContactSaleModalProps
> = ({ project, open, onClose }) => {
  const { t } = useTranslation();
  const data = useActionData<typeof action>();
  const { appointmentOpen, setAppointmentOpen, trackingTimeline } = useAppState(
    (state) => state
  );
  const { formNotice } = useRouteLoaderData("root") as IRouteLoaderData;
  useEffect(() => {
    const close = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
        setAppointmentOpen(false);
      }
    };
    if (open || appointmentOpen) {
      window.addEventListener("keydown", close);
    } else {
      window.removeEventListener("keydown", close);
    }
    return () => window.removeEventListener("keydown", close);
  }, [appointmentOpen, onClose, open, setAppointmentOpen]);

  // useEffect(() => {
  //   if (data?.ok) {
  //     onClose();
  //   }
  // }, [data?.ok, onClose]);

  const { appointmentTimeOptions, appointmentDateOptions } = useMemo(() => {
    const appointmentTimeOptions = generateTimeOptions("09:00", "21:00", 30);
    const appointmentDateOptions = generateDateOptions();
    return { appointmentDateOptions, appointmentTimeOptions };
  }, []);

  const defaultValues: Partial<TContactSaleFormData> = {
    interested: ["option1", "option2", "option3"],
    unitTypes: [],
    appointmentDate: appointmentDateOptions?.[0]?.id || "",
    appointmentTime: appointmentTimeOptions?.[0]?.id || "",
    formNotice: ["enquiRyFormNotice"],
  };

  const unitOptions: IOption[] =
    project?.unitTypes?.map((unit) => ({
      id: unit.slug,
      name: unit.title,
    })) || [];

  const handleClose = () => {
    onClose();
    setAppointmentOpen(false);
  };

  return (
    <>
      <Modal.Legacy
        open={open || appointmentOpen}
        className={cn(
          "w-11/12 max-w-[864px] !bg-enquiryBackground lg:max-h-[90dvh]"
        )}
        onClickBackdrop={handleClose}
      >
        <Modal.Header className="font-bold lg:mb-[8px]">
          <div className="flex flex-row justify-between">
            <div className="space-y-2">
              <Typo
                variant={ETypoVariant.HEADER_32}
                tag="h4"
                color={ETypoColor.ENQUIRY_HEADER}
                className="flex flex-1 items-center font-bold lg:text-header-18"
              >
                {t("secure_your_spot")}
              </Typo>
              <Typo
                variant={ETypoVariant.BODY_TITLE_16}
                tag="p"
                color={ETypoColor.ENQUIRY_HEADER}
                className="lg:text-body-title-12"
              >
                Register now for exclusive updates on this upcoming project.
              </Typo>
            </div>

            <IconClose onClick={handleClose} />
          </div>
        </Modal.Header>
        <Modal.Body>
          <div className="overflow-hidden">
            <Form<TContactSaleFormData>
              useFormProps={{ mode: "onBlur", defaultValues }}
              validationSchema={contactSalesSchema}
            >
              {({
                register,
                setValue,
                watch,
                formState: { isSubmitting, errors, isSubmitSuccessful },
              }) => {
                setValue("tracking", JSON.stringify(trackingTimeline));

                if (isSubmitSuccessful && data?.ok) {
                  handleClose();
                }
                return (
                  <div className="flex flex-col gap-6 !text-lg">
                    <CheckboxGroup
                      checkboxSize="md"
                      variant="enquiry"
                      label={t("i_am_interested_in")}
                      options={OPTIONS_CONTACT_SALE_INTERESTED}
                      register={register("interested")}
                      classListCheckbox="flex lg:flex-col flex-row gap-3 justify-between"
                    />
                    <div className="flex w-full flex-col gap-3">
                      <div className="flex gap-3 lg:flex-col">
                        <Input
                          variant="outlined"
                          placeholder="Name"
                          id="name"
                          className="h-[43px] flex-1 border-enquiryInputBorder bg-enquiryInputBackground px-[18px] py-3  text-base text-enquiryText lg:w-full lg:flex-auto"
                          {...register("name")}
                        />
                        {errors.name && (
                          <Typo
                            tag={ETypoTag.P}
                            variant={ETypoVariant.BODY_TITLE_14}
                            color={ETypoColor.ERROR}
                          >
                            {errors.name.message}
                          </Typo>
                        )}
                        <div className="flex flex-1 flex-col gap-1">
                          <Input
                            variant="outlined"
                            placeholder="Email"
                            id="email"
                            type="email"
                            className="h-[43px] flex-1 border-enquiryInputBorder bg-enquiryInputBackground px-[18px] py-3 text-base text-base text-enquiryText lg:w-full lg:flex-auto"
                            {...register("email")}
                          />
                          {errors.email && (
                            <Typo
                              tag={ETypoTag.P}
                              variant={ETypoVariant.BODY_TITLE_14}
                              color={ETypoColor.ERROR}
                            >
                              {errors.email.message}
                            </Typo>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-3 lg:flex-col">
                        <div className="flex w-1/3 flex-col gap-1 lg:w-full">
                          <div className="relative w-full">
                            <Input
                              variant="outlined"
                              placeholder="Phone"
                              type="tel"
                              id="phone"
                              className="h-[43px] w-full border-enquiryInputBorder bg-enquiryInputBackground py-3 pl-12 pr-[18px] text-base text-base text-enquiryText"
                              {...register("phone")}
                            />
                            <p className="absolute left-4 top-1/2 -translate-y-1/2 !text-base text-enquiryText">
                              {COUNTRY_PHONE_NUMBER_DIALCODE}
                            </p>
                          </div>
                          {errors.phone && (
                            <Typo
                              tag={ETypoTag.P}
                              variant={ETypoVariant.BODY_TITLE_14}
                              color={ETypoColor.ERROR}
                            >
                              {errors.phone.message}
                            </Typo>
                          )}
                        </div>
                        <SingleSelect
                          placeholder={t("choose_appointment_date")}
                          error={errors.appointmentDate?.message}
                          data={appointmentDateOptions}
                          title=""
                          name="appointmentDate"
                          variant="fill"
                          containerClass="w-2/3 flex-1 lg:w-full"
                          textSelectedClass="text-sub-title-16"
                          listboxButtonClass="border-enquiryInputBorder text-enquiryText bg-enquiryInputBackground h-[43px] px-[18px] py-3"
                          placeholderProps={{
                            variant: ETypoVariant.BODY_TITLE_16,
                            className:
                              "lg:!text-sub-title-14 !text-enquiryInputPlaceholder truncate",
                          }}
                          hasAnyOption={false}
                          dropdownClass="z-[1000000] mt-2 !max-h-[250px]"
                        />
                        <SingleSelect
                          placeholder={t("choose_appointment_time")}
                          error={errors.appointmentDate?.message}
                          data={appointmentTimeOptions}
                          title=""
                          name="appointmentTime"
                          variant="fill"
                          containerClass="w-1/3 flex-1 lg:w-full"
                          textSelectedClass="text-sub-title-16"
                          listboxButtonClass="border-enquiryInputBorder text-enquiryText bg-enquiryInputBackground h-[43px] px-[18px] py-3"
                          placeholderProps={{
                            variant: ETypoVariant.BODY_TITLE_16,
                            className:
                              "lg:!text-sub-title-14 !text-enquiryInputPlaceholder truncate",
                          }}
                          hasAnyOption={false}
                          dropdownClass="z-[1000000] mt-2 !max-h-[250px]"
                        />
                      </div>
                    </div>
                    <CheckboxGroup
                      checkboxSize="md"
                      variant="enquiry"
                      label={t("enquiry_form.unit_types")}
                      options={unitOptions}
                      register={register("unitTypes")}
                      classListCheckbox="grid grid-cols-3 lg:grid-cols-2 md:grid-cols-1 gap-6 w-full"
                    />
                    <CheckboxGroup
                      checkboxSize="md"
                      variant="enquiry"
                      options={[
                        {
                          id: "enquiRyFormNotice",
                          name: formNotice || "",
                          checkboxContainerClass: "items-start",
                        },
                      ]}
                      register={register("formNotice")}
                      classListCheckbox="grid grid-cols-1 w-full text-enquiryText"
                    />
                    <div className="flex w-full flex-col items-center justify-center gap-3">
                      <Button
                        type="submit"
                        className={cn(
                          "w-[280px] !rounded-[21.5px] !border !border-solid px-[18px] py-3 text-sub-title-16 font-bold lg:w-full lg:text-sub-title-14",
                          "border-enquirySubmitBackground bg-enquirySubmitBackground text-enquirySubmitText hover:border-enquirySubmitBackground hover:bg-enquirySubmitBackground active:hover:bg-enquirySubmitBackground active:focus:bg-enquirySubmitBackground"
                        )}
                        isLoading={isSubmitting}
                        disabled={watch("formNotice")?.length === 0}
                        // onClick={() => {
                        //   handleSubmit();
                        //   onClose();
                        // }}
                      >
                        <Typo
                          tag={ETypoTag.P}
                          variant={ETypoVariant.HEADER_16}
                          color={ETypoColor.ENQUIRY_SUBMIT}
                          className="font-bold capitalize"
                        >
                          {t("enquire_now")}
                        </Typo>
                      </Button>
                      <Link to={Slug.PRIVACY_POLICY}>
                        <Typo
                          tag="p"
                          variant={ETypoVariant.SUB_TITLE_16}
                          className="text-enquiryText underline lg:text-header-14"
                        >
                          {t("privacy_policy")}
                        </Typo>
                      </Link>
                    </div>
                  </div>
                );
              }}
            </Form>
          </div>
        </Modal.Body>
      </Modal.Legacy>
    </>
  );
};

export default ProjectDetailContactSale;
